import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { auth as authApi, ApiError, type AuthUser } from '../api/client'

interface AuthContextValue {
  user:              AuthUser | null
  isLoading:         boolean
  login:             (email: string, password: string) => Promise<void>
  register:          (email: string, password: string, displayName?: string) => Promise<void>
  loginWithResponse: (accessToken: string, userData: AuthUser) => void
  logout:            () => Promise<void>
  updateUser:        (updates: Partial<AuthUser>) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function readStoredUser(): AuthUser | null {
  const raw = localStorage.getItem('auth_user')
  if (!raw) return null
  try { return JSON.parse(raw) as AuthUser } catch { return null }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Lazy init reads localStorage synchronously — no effect or loading state needed
  const [user, setUser] = useState<AuthUser | null>(readStoredUser)
  const isLoading = false

  // Listen for token expiry (fired by the API client)
  useEffect(() => {
    const handleExpired = () => setUser(null)
    window.addEventListener('auth:expired', handleExpired)
    return () => window.removeEventListener('auth:expired', handleExpired)
  }, [])

  const persist = (accessToken: string, userData: AuthUser) => {
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('auth_user',    JSON.stringify(userData))
    setUser(userData)
  }

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password)
    persist(res.accessToken, res.user)
  }, [])

  const register = useCallback(async (email: string, password: string, displayName?: string) => {
    // Registration no longer returns a JWT — email verification is required first.
    // Callers should show a "check your inbox" message after this resolves.
    await authApi.register(email, password, displayName)
  }, [])

  const loginWithResponse = useCallback((accessToken: string, userData: AuthUser) => {
    persist(accessToken, userData)
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout()
    localStorage.removeItem('access_token')
    localStorage.removeItem('auth_user')
    setUser(null)
  }, [])

  const updateUser = useCallback((updates: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev
      const next = { ...prev, ...updates }
      localStorage.setItem('auth_user', JSON.stringify(next))
      return next
    })
  }, [])

  return (
    <AuthContext.Provider value={{ user, isLoading, login, register, loginWithResponse, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

// Re-export so callers can narrow error types without importing from two places
export { ApiError }
